import Ad from 'components/Common/Ad';
import useBreakpointMD from 'hooks/useBreakpointMD';
import React, {FC, Fragment, useEffect, useLayoutEffect, useRef, useState} from 'react';
import breakpoints from 'utils/breakpoints';
import {loadScriptsInSequence} from 'utils/domUtils';

import './styles.scss';

const VideoEmbed: FC<{embed: string; className?: string}> = ({
  embed,
  className,
}: {
  embed: string;
  className?: string;
}) => {
  const scriptContainerRef = useRef<HTMLDivElement>(null);
  const [scripts, setScripts] = useState<HTMLScriptElement[]>([]);
  const [cleanHtml, setCleanHtml] = useState<string>('');

  useEffect(() => {
    let html = embed;

    // update bitmovin player id to handle conflicts with timeline tab
    html = html.replace(/video-player-24media-\d+/g, '$&-videotab');
    const scriptMatches = Array.from(html.matchAll(/<script.*?>.*?<\/script>/gis));

    const newScripts: HTMLScriptElement[] = [];

    scriptMatches.forEach(([script]) => {
      if (!script) return;
      html = html.replace(script, '');

      const srcMatch = script.match(/<script.*?src="(?<src>.*?)".*?>.*?<\/script>/i);
      const contentMatch = script.match(/<script.*?>(?<content>.*?)<\/script>/is);

      if (!srcMatch && !contentMatch) return;

      const scriptEl = document.createElement('script');
      if (srcMatch && srcMatch?.groups?.src) {
        scriptEl.src = srcMatch.groups.src;
      } else if (contentMatch && contentMatch?.groups?.content) {
        scriptEl.innerHTML = contentMatch.groups.content;
      }
      newScripts.push(scriptEl);
    });
    if (newScripts.length) setScripts(newScripts);

    setCleanHtml(html);
  }, [embed]);

  useLayoutEffect(() => {
    if (scriptContainerRef.current) {
      const container = scriptContainerRef.current;
      loadScriptsInSequence(scripts, container);

      return () => {
        if (container) {
          scripts.forEach(scr => {
            return container;
          });
        }
      };
    }
    return undefined;
  }, [scriptContainerRef, scripts]);

  return (
    <div
      className={className ?? ''}
      dangerouslySetInnerHTML={{__html: cleanHtml}}
      ref={scriptContainerRef}
    />
  );
};

interface Props {
  videos: Array<{
    title: string;
    embed: string;
  }>;
}

const Videos: FC<Props> = ({videos}: Props) => {
  const isBreakpointMD = useBreakpointMD();

  return (
    <section className="Videos">
      {videos?.map(({title, embed}, index) => (
        <Fragment key={`${title} ${index + 1}`}>
          <article className="Videos__video">
            <VideoEmbed className="Videos__embed" embed={embed} />
            <div className="Videos__meta">
              <h2 className="Videos__title" dangerouslySetInnerHTML={{__html: title}} />
            </div>
          </article>
          {index === 0 && !isBreakpointMD && (
            <Ad
              adUnit="sport24.gr/Matchcenter_ros_300x250A"
              scope="matchpage"
              sizes={[
                [300, 600],
                [300, 250],
                [320, 100],
              ]}
              sizeMapping={[
                {
                  viewport: [breakpoints.md, 0],
                  sizes: [],
                },
                {
                  viewport: [0, 0],
                  sizes: [
                    [300, 600],
                    [300, 250],
                    [320, 100],
                  ],
                },
              ]}
            />
          )}
        </Fragment>
      ))}
    </section>
  );
};

export default Videos;
